/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-30
 */

import {
  Box,
  Typography,
  Button,
  Container,
  Badge,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Add local dependencies.
import { Studio } from '../../../types/studio';
import { StudiosGrid } from './StudiosGrid';
import {
  useGetMyJoinRequestsQuery,
  useSwitchStudioMutation,
} from '../../../features/studios/studiosApi';
import { JoinRequestStatus } from '../../../types/joinRequest';

interface MultipleStudiosViewProps {
  studios: Studio[];
  totalCount: number;
  onRefresh: () => void;
}

/**
 * MultipleStudiosView component for users who belong to multiple studios.
 * Shows a list of studios with activity indicators and pending join request status.
 */
const MultipleStudiosView: React.FC<MultipleStudiosViewProps> = ({
  studios,
  totalCount,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const [switchStudio] = useSwitchStudioMutation();

  // Get pending join requests to show indicators
  const { data: joinRequestsData } = useGetMyJoinRequestsQuery({
    pageSize: 50,
  });

  const pendingRequestsCount =
    joinRequestsData?.results?.filter(
      request => request.status === JoinRequestStatus.PENDING
    ).length || 0;

  const handleAddStudio = () => {
    navigate('/studios/add');
  };

  const handleStudioSelect = async (studioId: string) => {
    try {
      // Switch studio context on backend before navigation
      await switchStudio(studioId).unwrap();
      navigate(`/studio/${studioId}`);
    } catch (error) {
      console.error('Failed to switch studio:', error);
      // Still navigate even if switch fails - user can try refreshing
      navigate(`/studio/${studioId}`);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              My Studios
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {totalCount} {totalCount === 1 ? 'studio' : 'studios'} found
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
            >
              Refresh
            </Button>
            <Badge
              badgeContent={pendingRequestsCount}
              color="primary"
              invisible={pendingRequestsCount === 0}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddStudio}
              >
                Add Studio
              </Button>
            </Badge>
          </Box>
        </Box>

        {/* Pending Join Requests Alert */}
        {pendingRequestsCount > 0 && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate('/studios/add?tab=requests')}
              >
                View Requests
              </Button>
            }
          >
            You have {pendingRequestsCount} pending join{' '}
            {pendingRequestsCount === 1 ? 'request' : 'requests'} waiting for
            review.
          </Alert>
        )}

        {/* Studios Grid */}
        <StudiosGrid studios={studios} onStudioSelect={handleStudioSelect} />
      </Box>
    </Container>
  );
};

export default MultipleStudiosView;
